<script setup lang="ts">
import JsonEditor from "@/components/JsonEditor/JsonEditor.vue";
import MultiSelectWithChips from "@/components/MultiSelectWithChips/MultiSelectWithChips.vue";
import { FormRule, VdFormField, VdInput, VdInputVersion, VdSelect, VdSwitch } from "@voodoo.io/voodoo-ui";
import InputNumber from "primevue/inputnumber";
import { PropType, computed, ref, useAttrs, watch } from "vue";

const emit = defineEmits(["update:modelValue"]);

const props = defineProps({
  /**
   * The CSS class
   */
  class: {
    type: [String, Object] as PropType<string | Record<string, boolean>>,
    default: "",
  },

  /**
   * The type of the input
   */
  type: {
    type: String,
    default: "text",
    validate: (value: string) =>
      [
        "text",
        "number",
        "integer",
        "float",
        "multiselect",
        "select",
        "switch",
        "boolean",
        "tags",
        "string[]",
        "integer[]",
        "float[]",
        "tags",
        "version",
      ].includes(value),
  },

  /**
   * The value binding (v-model)
   */
  modelValue: {
    type: null,
    default: null,
  },

  /**
   * A key of the form object
   */
  prop: {
    type: String,
    required: true,
  },

  /**
   * The label of the input.
   */
  label: {
    type: String,
    default: "",
  },

  /**
   * A tooltip with a helpful text.
   */
  helpText: {
    type: String,
    default: "",
  },

  /**
   * The validation rule of the form field
   */
  rules: {
    type: Array as PropType<FormRule[]>,
    default: () => [],
  },

  /**
   * Display the error message under the input.
   */
  error: {
    type: String,
    default: "",
  },

  /**
   * If true, the value will be required.
   * An asterisk will be added to the form.
   */
  required: {
    type: Boolean,
  },
});

const isNumber = computed(() => ["integer", "number"].includes(props.type));
const isFloat = computed(() => ["float"].includes(props.type));

const numberType = computed(() => {
  if (props.type === "float") {
    return "number";
  }
  return "integer";
});

const isBoolean = computed(() => ["boolean", "switch"].includes(props.type));
const isEditableMultiselect = computed(() => ["tags", "string[]", "integer[]", "float[]"].includes(props.type));
const isMultiSelect = computed(() => ["multiselect"].includes(props.type));
const isVersion = computed(() => props.type === "version");
const isSelect = computed(() => props.type === "select");

const innerValue = ref();

watch(
  () => props.modelValue,
  (modelValue) => {
    if (["integer[]", "float[]"].includes(props.type)) {
      innerValue.value = modelValue.map((item: number) => item.toString());
    } else {
      innerValue.value = modelValue;
    }
  },
  {
    immediate: true,
  }
);

watch(innerValue, (value) => {
  let newValue = value;

  if (["string[]", "integer[]", "float[]"].includes(props.type)) {
    return;
  }

  if (props.type === "text") {
    newValue = value.trim();
  }

  emit("update:modelValue", newValue);
});

const classValue = computed(() => {
  const result = {
    "versatile-form-field-component": true,
  };
  if (typeof props.class === "string") {
    return { ...result, [props.class]: true };
  }
  return { ...result, ...props.class };
});

const attrs = useAttrs();

const onMultiSelectValueChange = (value: string[]) => {
  if (props.type === "string[]") {
    const newValue = value
      .map((item) => item.split(","))
      .reduce((acc, val) => acc.concat(val), [])
      .map((item) => item.trim())
      .filter((item) => item !== "");

    innerValue.value = newValue;
    emit("update:modelValue", newValue);
  }

  if (props.type === "integer[]" || props.type === "float[]") {
    const newValue = value
      .map((v: string) => v.toString())
      .map((item) => item.split(","))
      .reduce((acc, val) => acc.concat(val), [])
      .map((item) => item.trim())
      .filter((item) => item !== "")
      .map((v: string) => {
        const parsedValue = props.type === "integer[]" ? parseInt(v, 10) : parseFloat(v);
        return parsedValue;
      })
      .filter((v: number) => !Number.isNaN(v));

    innerValue.value = newValue.map((item: number) => item.toString());
    emit("update:modelValue", newValue);
  }
};

const clearValues = () => {
  innerValue.value = [];
  emit("update:modelValue", []);
};
</script>

<template>
  <VdFormField
    :class="classValue"
    :prop="prop"
    :help-text="helpText"
    :rules="rules"
    :error="error"
    :required="required"
    :label="label"
  >
    <VdSwitch v-if="isBoolean" v-model="innerValue" v-bind="attrs" />
    <VdSelect v-else-if="isSelect" v-model="innerValue" v-bind="attrs" />
    <VdSelect v-else-if="isMultiSelect" v-model="innerValue" v-bind="attrs" multiple />
    <MultiSelectWithChips
      v-else-if="isEditableMultiselect"
      v-model="innerValue"
      :placeholder="innerValue && innerValue.length > 0 ? '' : 'Separate values with a comma and press enter'"
      :disabled="(attrs.disabled as boolean)"
      @clear-values="clearValues"
      @value-change="onMultiSelectValueChange"
    />
    <VdInputVersion v-else-if="isVersion" v-model="innerValue" v-bind="attrs" />
    <VdInput v-else-if="isNumber" v-model="innerValue" v-bind="attrs" :type="numberType" />
    <InputNumber v-else-if="isFloat" v-model="innerValue" locale="en-US" :max-fraction-digits="5" v-bind="attrs" />
    <JsonEditor
      v-else-if="props.type === 'json'"
      v-model="innerValue"
      :readonly="false"
      :disabled="(attrs.disabled as boolean)"
    />
    <VdInput v-else v-model="innerValue" v-bind="attrs" />
  </VdFormField>
</template>
